import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { ArrowRight, Calendar, Clock, Rocket, Shield, TrendingUp } from "lucide-react";
import { HeroBackdropVideo, HERO_VIDEO_POSTER_WEBP } from "@/components/hero/HeroBackdropVideo";
import { HeroBeforeAfter } from "@/components/hero/HeroBeforeAfter";
import { HeroJobFlow } from "@/components/hero/HeroJobFlow";
import { HeroSlideControls } from "@/components/hero/HeroSlideControls";
import { HomeAudiences } from "@/components/hero/HomeAudiences";
import { isHeroSlidesCopy } from "@/components/hero/heroSlideCopy";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HeroTrustLine } from "@/components/hero/HeroTrustLine";
import PeppolTrustBadge from "@/components/PeppolTrustBadge";
import { BookDemoAnchor } from "@/components/BookDemoAnchor";
import { HERO_SLIDES, useHeroCarousel } from "@/hooks/useHeroCarousel";
import { useRotatingCTA } from "@/hooks/useRotatingCTA";
import { cn } from "@/lib/utils";
import { trackButtonClick } from "@/utils/analytics";

const DESKTOP_MEDIA = "(min-width: 1024px)";
const MOBILE_MEDIA = "(max-width: 1023px)";

/** Pauses the slideshow and the video while the hero is scrolled away. */
function useInView<T extends Element>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.15,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, inView] as const;
}

function isKeyboardFocus(element: HTMLElement): boolean {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true; // Browsers without :focus-visible: treat every focus as keyboard focus.
  }
}

const HeroSection = () => {
  const { t } = useTranslation();
  const { getAppUrl, getPeekDemoUrl, openDemoModal } = useRotatingCTA(false);
  const heroTitles = t("pages.home.hero.rotatingTitles", { returnObjects: true }) as Array<{
    main: string;
    highlight: string;
  }>;
  const benefits = t("pages.home.hero.benefits", { returnObjects: true }) as string[];
  const rawSlides = t("pages.home.heroSlides", { returnObjects: true });
  const slides = isHeroSlidesCopy(rawSlides) ? rawSlides : null;
  const flowSteps = slides?.flow.steps ?? [];

  const [sectionRef, inView] = useInView<HTMLElement>();
  const carousel = useHeroCarousel({ flowSteps: flowSteps.length, inView });
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const videoActive = carousel.slide === "video" && inView;

  useEffect(() => {
    if (heroTitles.length < 2 || carousel.reducedMotion) return;
    const interval = setInterval(() => {
      setCurrentTitleIndex((prev) => (prev + 1) % heroTitles.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [heroTitles.length, carousel.reducedMotion]);

  const currentTitle = heroTitles[currentTitleIndex] || heroTitles[0];
  const selectSlide = (index: number) => {
    carousel.show(index);
    trackButtonClick(`hero-slide-${HERO_SLIDES[index]}`, "home-hero");
  };
  const slideProps = (slide: (typeof HERO_SLIDES)[number]) => ({
    id: `hero-slide-${slide}`,
    className: cn("hero-slide", carousel.slide === slide && "is-active"),
    "aria-hidden": carousel.slide !== slide,
    "data-slide": slide,
  });

  return (
    <section
      ref={sectionRef}
      className="hero-section hero-gradient relative overflow-hidden pb-12 pt-6 sm:pb-16 sm:pt-10 lg:pb-16 lg:pt-32"
      style={{ marginTop: "var(--menu-offset, 100px)" }}
      data-slide={carousel.slide}
      data-home-hero
    >
      <Helmet>
        <link rel="preload" as="image" type="image/webp" href={HERO_VIDEO_POSTER_WEBP} media="(min-width: 640px)" fetchPriority="high" />
      </Helmet>

      <div className="hero-backdrop" aria-hidden="true">
        <HeroBackdropVideo active={videoActive} placement={DESKTOP_MEDIA} />
        <div className="hero-backdrop-scrim" />
      </div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="hero-blob absolute top-10 sm:top-16 -left-20 sm:left-10 w-40 sm:w-56 h-40 sm:h-56 bg-primary/5 rounded-full blur-3xl" />
        <div className="hero-blob-delayed absolute bottom-10 sm:bottom-16 -right-20 sm:right-10 w-48 sm:w-72 h-48 sm:h-72 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="hero-layout">
          <div className="hero-copy min-w-0 space-y-6">
            <div className="flex items-center justify-center lg:justify-start">
              <Badge variant="secondary" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold bg-primary/10 text-primary border-primary/20 backdrop-blur-sm">
                <Rocket className="w-3 sm:w-4 h-3 sm:h-4 mr-2" />
                {t("pages.home.hero.badge")}
              </Badge>
            </div>

            <div className="space-y-3 sm:space-y-4 text-center lg:text-left">
              <h1
                className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl text-foreground leading-tight font-bold"
                data-testid="hero-heading"
              >
                {t("landing.hero.title")}
              </h1>
              {currentTitle && (
                <h2 className="text-lg sm:text-xl md:text-2xl text-muted-foreground leading-relaxed font-medium">
                  {currentTitle.main}{" "}
                  <span className="text-primary">{currentTitle.highlight}</span>
                </h2>
              )}
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                {t("landing.hero.subtitle")}
              </p>
            </div>

            <div className="hero-copy-actions space-y-6">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
                {[
                  { icon: TrendingUp, text: benefits[0] },
                  { icon: Clock, text: benefits[1] },
                  { icon: Shield, text: benefits[2] },
                ].map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 text-xs sm:text-sm p-2 rounded-lg bg-background/70 backdrop-blur-sm border border-border/50"
                  >
                    <div className="w-6 sm:w-7 h-6 sm:h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-3 sm:w-4 h-3 sm:h-4 text-primary" />
                    </div>
                    <span className="text-muted-foreground font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap justify-center lg:justify-start">
                  <Button asChild variant="hero" className="w-full sm:w-auto touch-manipulation group">
                    <a href={getAppUrl()} target="_blank" rel="noopener noreferrer" data-testid="button-hero-cta">
                      {t("pages.home.hero.primaryCta")}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </Button>
                  <Button asChild variant="demo" className="w-full sm:w-auto touch-manipulation">
                    <BookDemoAnchor source="home" data-testid="button-hero-demo">
                      <Calendar className="w-4 h-4 mr-2" />
                      {t("pages.home.hero.demoButton")}
                    </BookDemoAnchor>
                  </Button>
                </div>
                <a
                  href={getPeekDemoUrl()}
                  onClick={openDemoModal("home-peek", "home-peek")}
                  className="block text-sm text-muted-foreground text-center lg:text-left hover:underline underline-offset-4"
                  data-testid="link-hero-peek"
                >
                  {t("pages.home.hero.peekLink")}
                </a>
              </div>

              <div className="hero-secondary">
                <div className="flex items-center justify-center lg:justify-start gap-3">
                  <div className="flex -space-x-1" data-testid="hero-trust-stars">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="w-6 sm:w-7 h-6 sm:h-7 bg-primary/20 rounded-full border-2 border-background flex items-center justify-center">
                        <span className="text-xs font-bold text-primary">★</span>
                      </div>
                    ))}
                  </div>
                  <HeroTrustLine />
                </div>
                <div className="flex justify-center lg:justify-start" data-testid="hero-peppol-badge">
                  <PeppolTrustBadge tone="light" />
                </div>
              </div>
            </div>
          </div>

          {slides && (
            <div
              className="hero-visual min-w-0"
              onPointerEnter={(event) => event.pointerType === "mouse" && carousel.setHovered(true)}
              onPointerLeave={(event) => event.pointerType === "mouse" && carousel.setHovered(false)}
              onFocus={(event) => {
                // Keyboard focus pauses rotation; the play/pause toggle itself never does.
                const target = event.target as HTMLElement;
                carousel.setFocused(isKeyboardFocus(target) && !target.closest(".hero-slide-toggle, .hero-slide-chip-toggle"));
              }}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  carousel.setFocused(false);
                }
              }}
              data-testid="hero-visual"
            >
              <div className="hero-stage">
                <div {...slideProps("video")}>
                  <figure className="hero-video-frame">
                    <HeroBackdropVideo active={videoActive} placement={MOBILE_MEDIA} />
                    <figcaption>{slides.videoCaption}</figcaption>
                  </figure>
                </div>
                <div {...slideProps("flow")}>
                  <HeroJobFlow
                    steps={flowSteps}
                    current={carousel.flowStep}
                    ariaLabel={slides.flow.ariaLabel}
                    onSelect={carousel.showStep}
                  />
                </div>
                <div {...slideProps("beforeAfter")}>
                  <HeroBeforeAfter
                    copy={slides.beforeAfter}
                    active={carousel.slide === "beforeAfter"}
                    reducedMotion={carousel.reducedMotion}
                    onInteract={carousel.stop}
                  />
                </div>
              </div>

              <HeroSlideControls
                labels={slides.tabs}
                groupLabel={slides.groupLabel}
                pauseLabel={slides.pause}
                playLabel={slides.play}
                nextLabel={(label) => t("pages.home.heroSlides.next", { label })}
                viewLabel={slides.view}
                index={carousel.index}
                flowStep={carousel.flowStep}
                flowSteps={flowSteps.length}
                autoplay={carousel.autoplay}
                playing={carousel.playing}
                reducedMotion={carousel.reducedMotion}
                clockKey={carousel.clockKey}
                clockMs={carousel.clockMs}
                onTick={carousel.tick}
                onSelect={selectSlide}
                onToggle={carousel.toggleAutoplay}
              />
            </div>
          )}
        </div>

        <div className="hero-audiences">
          <HomeAudiences />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
